import tokenStore from "common/tokenStore";

// Identifier this app registers under in the centralized id service allowlist
// (REACT_APP_AUTHORIZED_APPS in the id project).
const APP_ID = "customerportaladmin";

// Where the PKCE verifier is held between initiating login and the callback.
const PKCE_VERIFIER_KEY = "didimo.admin.pkceVerifier";

const API_ROOT = process.env.REACT_APP_API_ROOT;
// Base URL of the centralized id service; baked in per-environment at build time.
const ID_ROOT = process.env.REACT_APP_ID_ROOT;

// --- PKCE (RFC 7636, S256) -------------------------------------------------
// base64url without padding, matching the API's WebEncoders.Base64UrlEncode.
const base64UrlEncode = (bytes) => {
    const arr = new Uint8Array(bytes);
    let binary = "";
    for (let i = 0; i < arr.length; i++) binary += String.fromCharCode(arr[i]);
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

// A high-entropy, URL-safe verifier (43 chars).
const createVerifier = () => {
    const arr = new Uint8Array(32);
    window.crypto.getRandomValues(arr);
    return base64UrlEncode(arr);
};

const createChallenge = async (verifier) => {
    const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
    return base64UrlEncode(digest);
};

// The id login URL, carrying the PKCE challenge so the API binds the issued
// code to a secret only this browser holds. The id service authenticates, then
// redirects back to `${APP_ROOT}/profile/login?code=<code>`.
const buildLoginUrl = (codeChallenge) => {
    const url = new URL(`${ID_ROOT}/login/${APP_ID}`);
    if (codeChallenge) url.searchParams.set("code_challenge", codeChallenge);
    return url.toString();
};

// Begin sign-in: mint a PKCE pair, stash the verifier for the callback, and
// hand off to the id service.
export const startLogin = async () => {
    const verifier = createVerifier();
    const challenge = await createChallenge(verifier);
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    window.location.href = buildLoginUrl(challenge);
};

// Where to send the browser to end the id (IdP) session after clearing local
// tokens. Returns to a public post-logout landing that does NOT auto-login,
// so the sign-out is observable and the next login prompts for credentials.
export const getLogoutUrl = () => {
    const returnTo = `${window.location.origin}/profile/login?loggedout=1`;
    return `${ID_ROOT}/logout?returnTo=${encodeURIComponent(returnTo)}`;
};

// Exchange the single-use code (from ?code=) for the auth tokens, proving
// possession of the PKCE verifier. credentials:include lets the API set the
// HttpOnly refresh cookie (the refresh token never touches JS).
// Backend: GET /v3/auth/tokens/{code} -> { currentDate, tokens: { id_token, access_token } }.
export const exchangeCode = async (code) => {
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    const url = new URL(`${API_ROOT}v3/auth/tokens/${encodeURIComponent(code)}`);
    url.searchParams.set("client_type", "web");
    if (verifier) url.searchParams.set("code_verifier", verifier);

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
    });
    // Single-use verifier, whatever the outcome.
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);

    if (!response.ok) {
        throw new Error(`Code exchange failed with status ${response.status}`);
    }
    const data = await response.json();
    // Tolerate camelCase or PascalCase serialization from the API.
    return data.tokens || data.Tokens;
};

// Silent renewal. The refresh token lives in an HttpOnly cookie, so we just
// call /auth/refresh with credentials; the Authorization header (current access
// token) is the CSRF double-submit the API requires. Concurrent callers share a
// single in-flight request. Returns the new tokens, or null on failure.
let refreshInFlight = null;
export const refreshTokens = () => {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
        try {
            const url = new URL(`${API_ROOT}v3/auth/refresh`);
            url.searchParams.set("client_type", "web");
            url.searchParams.set("target_app", APP_ID);
            const response = await fetch(url.toString(), {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${tokenStore.getAccessToken() || ""}`,
                },
                credentials: "include",
            });
            if (!response.ok) return null;
            const data = await response.json();
            const tokens = data.tokens || data.Tokens;
            if (!tokens?.access_token) return null;
            tokenStore.setTokens(tokens);
            return tokens;
        } catch (e) {
            console.error("Token refresh failed", e);
            return null;
        } finally {
            refreshInFlight = null;
        }
    })();
    return refreshInFlight;
};

// Renew the access token this many ms *before* it actually expires, so a
// request initiated while the token is still (barely) valid doesn't arrive at
// the API already expired.
const TOKEN_REFRESH_SKEW_MS = 30 * 1000;

// Pre-request hook: if the access token is missing or within the skew window
// of expiry but a session still exists, renew it silently before the caller
// fires its request. Concurrent callers coalesce onto the single in-flight
// refresh above, so this is cheap to call on every request. No session (never
// logged in / fully logged out) -> no-op, and the request proceeds unauthenticated.
export const ensureFreshToken = async () => {
    if (!tokenStore.isAuthenticated(TOKEN_REFRESH_SKEW_MS) && tokenStore.getTokens()) {
        await refreshTokens();
    }
};
